import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Switch, TextField } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react'
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { toastMessage } from '../../utils/toastMessage';
import { updateUserMasterSettingsApi, getUserMasterSettingsApi, uploadFixedSessionImageApi, uploadSettingsImageApi, getSystemAudioUsageApi, updateSystemAudioUsageApi } from '../../services/dashboard';
import { trackActivityApi } from '../../services/activityLog';

const BLANK = '';
const SETTINGS_BUTTON_STYLE = { backgroundColor: '#f28b8f', borderColor: '#f28b8f', color: '#fff' };
const SETTINGS_TABLE_BORDER = '#d8deea';
const SETTINGS_TABLE_HEADER_BG = '#f4f7fc';

const safeTrack = async (payload) => {
    try {
        const email = localStorage.getItem("vedic_email") || "Admin";
        await trackActivityApi({
            action: payload.action || "UPDATE",
            module: "Settings",
            description: payload.description || `${email} updated Settings`,
            resourceId: "",
            resourceType: "",
            metadata: payload.metadata || {},
            status: payload.status,
            responseStatus: payload.responseStatus,
        });
    } catch (_e) {
        // non-blocking
    }
};

export default function AppTogglers() {

    const [forAndroidFirstFree, setForAndroidFirstFree] = useState({
        isFirst5MinFree: true,
        isOnCallReviewCanExtend: true
    });

    const [foriOSFirstFree, setForiOSFirstFree] = useState({
        isFirst5MinFree: true
    });

    const updateForAndroidFirstFree = async () => {
        try {
            const email = localStorage.getItem("vedic_email") || "Admin";
            const payload = {
                isFirst5MinFree: forAndroidFirstFree.isFirst5MinFree,
                isOnCallReviewCanExtend: forAndroidFirstFree.isOnCallReviewCanExtend
            };
            const response = await updateUserMasterSettingsApi('firstFreeConsultantForAndroid', payload);
            await safeTrack({
                action: "UPDATE",
                description: `${email} clicked "Update For Android" (Free Consultation Settings)`,
                status: response?.success ? "success" : "failed",
                responseStatus: response?.code || (response?.success ? 200 : 400),
                metadata: {
                    document: "firstFreeConsultantForAndroid",
                    payload,
                    apiResponse: response,
                },
            });
            toastMessage(response?.message || 'Successfully updated', "success");
        } catch (error) {
            const email = localStorage.getItem("vedic_email") || "Admin";
            await safeTrack({
                action: "UPDATE",
                description: `${email} clicked "Update For Android" (Free Consultation Settings)`,
                status: "failed",
                responseStatus: 500,
                metadata: {
                    document: "firstFreeConsultantForAndroid",
                    payload: {
                        isFirst5MinFree: forAndroidFirstFree.isFirst5MinFree,
                        isOnCallReviewCanExtend: forAndroidFirstFree.isOnCallReviewCanExtend
                    },
                    error: String(error?.message || error),
                },
            });
            toastMessage(error?.message, "error");
        }
    }

    const updateForiOSFirstFree = async () => {
        try {
            const email = localStorage.getItem("vedic_email") || "Admin";
            const payload = {
                isFirst5MinFree: foriOSFirstFree.isFirst5MinFree
            };
            const response = await updateUserMasterSettingsApi('firstFreeConsultantForIos', payload);
            await safeTrack({
                action: "UPDATE",
                description: `${email} clicked "Update For iOS" (Free Consultation Settings)`,
                status: response?.success ? "success" : "failed",
                responseStatus: response?.code || (response?.success ? 200 : 400),
                metadata: {
                    document: "firstFreeConsultantForIos",
                    payload,
                    apiResponse: response,
                },
            });
            toastMessage(response?.message || 'Successfully updated', "success");
        } catch (error) {
            const email = localStorage.getItem("vedic_email") || "Admin";
            await safeTrack({
                action: "UPDATE",
                description: `${email} clicked "Update For iOS" (Free Consultation Settings)`,
                status: "failed",
                responseStatus: 500,
                metadata: {
                    document: "firstFreeConsultantForIos",
                    payload: { isFirst5MinFree: foriOSFirstFree.isFirst5MinFree },
                    error: String(error?.message || error),
                },
            });
            toastMessage(error?.message, "error");
        }
    }

    useEffect(() => {   
        (async () => {
            try {
                const { data: _androidResponse } = await getUserMasterSettingsApi('firstFreeConsultantForAndroid');
                if (_androidResponse?.success) {
                    setForAndroidFirstFree({
                        isFirst5MinFree: _androidResponse?.data?.data?.isFirst5MinFree,
                        isOnCallReviewCanExtend: _androidResponse?.data?.data?.isOnCallReviewCanExtend
                    })
                }
            } catch (error) {
                console.error('Error fetching Android first free consultation settings:', error);
            }
            
            try {
                const { data: _iosResponse } = await getUserMasterSettingsApi('firstFreeConsultantForIos');
                if (_iosResponse?.success) {
                    setForiOSFirstFree({
                        isFirst5MinFree: _iosResponse?.data?.data?.isFirst5MinFree
                    })
                }
            } catch (error) {
                console.error('Error fetching iOS first free consultation settings:', error);
            }
        })()
    }, []);

    return (
        <div className="py-4">
            <div className="d-flex flex-wrap gap-3 mb-3">
                <div className="flex-fill" style={{ minWidth: '300px', flexBasis: 'calc(50% - 0.75rem)' }}>
                    <div className="card shadow-sm">
                        <div className="card-body">
                            <h5 className="card-title mb-3">Android Free Consultation Settings</h5>
                            <div className="mb-2 d-flex align-items-center justify-content-between">
                                <span>Is First 5 Min Free:</span>
                                <span className={`badge ${forAndroidFirstFree.isFirst5MinFree ? 'bg-success' : 'bg-danger'}`}>
                                    {forAndroidFirstFree.isFirst5MinFree ? 'true' : 'false'}
                                </span>
                            </div>
                            <div className="mb-2 d-flex align-items-center justify-content-between">
                                <span>Is On Call Review Can Extend:</span>
                                <span className={`badge ${forAndroidFirstFree.isOnCallReviewCanExtend ? 'bg-success' : 'bg-danger'}`}>
                                    {forAndroidFirstFree.isOnCallReviewCanExtend ? 'true' : 'false'}
                                </span>
                            </div>
                            <div className="mb-3">
                                <button
                                    className="btn w-100"
                                    style={SETTINGS_BUTTON_STYLE}
                                    onClick={updateForAndroidFirstFree}
                                >
                                    Update For Android
                                </button>
                            </div>
                            <div className="mb-2 d-flex align-items-center justify-content-between">
                                <label className="form-label mb-0">First 5 Min Free</label>
                                <Switch
                                    checked={forAndroidFirstFree.isFirst5MinFree}
                                    onChange={() => {
                                        const email = localStorage.getItem("vedic_email") || "Admin";
                                        const from = !!forAndroidFirstFree.isFirst5MinFree;
                                        setForAndroidFirstFree(_e => {
                                            if (_e.isFirst5MinFree) {
                                                return {
                                                    ..._e,
                                                    isFirst5MinFree: false,
                                                    isOnCallReviewCanExtend: false
                                                }
                                            }
                                            return {
                                                ..._e,
                                                isFirst5MinFree: !_e.isFirst5MinFree
                                            }
                                        })
                                        safeTrack({
                                            action: "TOGGLE",
                                            description: `${email} toggled "First 5 Min Free" (Android)`,
                                            status: "success",
                                            responseStatus: 200,
                                            metadata: {
                                                document: "firstFreeConsultantForAndroid",
                                                control: "isFirst5MinFree",
                                                from,
                                                to: !from,
                                            },
                                        });
                                    }}
                                />
                            </div>
                            {forAndroidFirstFree.isFirst5MinFree && (
                                <div className="mb-2 d-flex align-items-center justify-content-between">
                                    <label className="form-label mb-0">Can Extend 1st Chat</label>
                                    <Switch
                                        checked={forAndroidFirstFree.isOnCallReviewCanExtend}
                                        onChange={() =>
                                            (() => {
                                                const email = localStorage.getItem("vedic_email") || "Admin";
                                                const from = !!forAndroidFirstFree.isOnCallReviewCanExtend;
                                                setForAndroidFirstFree({
                                                    ...forAndroidFirstFree,
                                                    isOnCallReviewCanExtend: !forAndroidFirstFree.isOnCallReviewCanExtend
                                                });
                                                safeTrack({
                                                    action: "TOGGLE",
                                                    description: `${email} toggled "Can Extend 1st Chat" (Android)`,
                                                    status: "success",
                                                    responseStatus: 200,
                                                    metadata: {
                                                        document: "firstFreeConsultantForAndroid",
                                                        control: "isOnCallReviewCanExtend",
                                                        from,
                                                        to: !from,
                                                    },
                                                });
                                            })()
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex-fill" style={{ minWidth: '300px', flexBasis: 'calc(50% - 0.75rem)' }}>
                    <div className="card shadow-sm">
                        <div className="card-body">
                            <h5 className="card-title mb-3">iOS Free Consultation Settings</h5>
                            <div className="mb-2 d-flex align-items-center justify-content-between">
                                <span>Is First 5 Min Free:</span>
                                <span className={`badge ${foriOSFirstFree.isFirst5MinFree ? 'bg-success' : 'bg-danger'}`}>
                                    {foriOSFirstFree.isFirst5MinFree ? 'true' : 'false'}
                                </span>
                            </div>
                            <div className="mb-3">
                                <button
                                    className="btn w-100"
                                    style={SETTINGS_BUTTON_STYLE}
                                    onClick={updateForiOSFirstFree}
                                >
                                    Update For iOS
                                </button>
                            </div>
                            <div className="mb-2 d-flex align-items-center justify-content-between">
                                <label className="form-label mb-0">First 5 Min Free</label>
                                <Switch
                                    checked={foriOSFirstFree.isFirst5MinFree}
                                    onChange={() => {
                                        const email = localStorage.getItem("vedic_email") || "Admin";
                                        const from = !!foriOSFirstFree.isFirst5MinFree;
                                        setForiOSFirstFree({
                                            isFirst5MinFree: !foriOSFirstFree.isFirst5MinFree
                                        })
                                        safeTrack({
                                            action: "TOGGLE",
                                            description: `${email} toggled "First 5 Min Free" (iOS)`,
                                            status: "success",
                                            responseStatus: 200,
                                            metadata: {
                                                document: "firstFreeConsultantForIos",
                                                control: "isFirst5MinFree",
                                                from,
                                                to: !from,
                                            },
                                        });
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export const AudioCallSettingsToggler = () => {
    const [systemAudioUsage, setSystemAudioUsage] = useState('exotel');
    const [saving, setSaving] = useState(false);

    const loadSystemAudioUsage = async () => {
        const response = await getSystemAudioUsageApi();
        const value = response?.data?.result?.systemAudioUsage;
        if (value === 'livekit' || value === 'exotel') {
            setSystemAudioUsage(value);
        }
    };

    const switchSystemAudioUsage = async () => {
        const email = localStorage.getItem("vedic_email") || "Admin";
        const nextValue = systemAudioUsage === 'livekit' ? 'exotel' : 'livekit';
        setSaving(true);
        try {
            const response = await updateSystemAudioUsageApi(nextValue);
            const saved = response?.data?.success === true;
            if (saved) {
                setSystemAudioUsage(response?.data?.result?.systemAudioUsage || nextValue);
            }
            await safeTrack({
                action: "UPDATE",
                description: `${email} switched systemAudioUsage to ${nextValue}`,
                status: saved ? "success" : "failed",
                responseStatus: response?.data?.code || (saved ? 200 : 400),
                metadata: {
                    collection: "masters",
                    key: "systemAudioUsage",
                    from: systemAudioUsage,
                    to: nextValue,
                    apiResponse: response?.data,
                },
            });
            toastMessage(
                response?.data?.message || (saved ? 'System audio usage updated' : 'Failed to update audio call'),
                saved ? "success" : "error"
            );
        } catch (error) {
            await safeTrack({
                action: "UPDATE",
                description: `${email} switched systemAudioUsage to ${nextValue}`,
                status: "failed",
                responseStatus: 500,
                metadata: {
                    collection: "masters",
                    key: "systemAudioUsage",
                    from: systemAudioUsage,
                    to: nextValue,
                    error: String(error?.message || error),
                },
            });
            toastMessage(error?.response?.data?.message || error?.message, "error");
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        loadSystemAudioUsage().catch((error) => {
            console.error('Error fetching system audio usage:', error);
        });
    }, []);

    const isLiveKit = systemAudioUsage === 'livekit';

    return (
        <div className="card shadow-sm">
            <div className="card-body">
                <h5 className="card-title mb-3">Audio Call Provider</h5>
                <div className="mb-3 d-flex align-items-center justify-content-between">
                    <span>systemAudioUsage:</span>
                    <span className={`badge ${isLiveKit ? 'bg-success' : 'bg-secondary'}`}>
                        {systemAudioUsage}
                    </span>
                </div>
                <button
                    className="btn w-100"
                    style={SETTINGS_BUTTON_STYLE}
                    onClick={switchSystemAudioUsage}
                    disabled={saving}
                >
                    {saving ? 'Updating...' : (isLiveKit ? 'Switch to Exotel' : 'Switch to LiveKit')}
                </button>
            </div>
        </div>
    );
};

export const ConsultantSessionInfoToggler = () => {
    const [master, setMaster] = useState({
        isSessionHelperInfoActive: true,
        consultantOnlineTimeTarget: 5 // Default target in hours per day
    });

    const updateMaster = async () => {
        const email = localStorage.getItem("vedic_email") || "Admin";
        const payload = {
            isSessionHelperInfoActive: master.isSessionHelperInfoActive,
            consultantOnlineTimeTarget: master.consultantOnlineTimeTarget
        };
        try {
            const response = await updateUserMasterSettingsApi('consultantMasterSettings', payload);
            await safeTrack({
                action: "UPDATE",
                description: `${email} clicked "Update" (Consultant Master Settings)`,
                status: response?.success ? "success" : "failed",
                responseStatus: response?.code || (response?.success ? 200 : 400),
                metadata: {
                    document: "consultantMasterSettings",
                    payload,
                    apiResponse: response,
                },
            });
            toastMessage(response?.message || 'Successfully updated', "success");
        } catch (error) {
            await safeTrack({
                action: "UPDATE",
                description: `${email} clicked "Update" (Consultant Master Settings)`,
                status: "failed",
                responseStatus: 500,
                metadata: {
                    document: "consultantMasterSettings",
                    payload,
                    error: String(error?.message || error),
                },
            });
            toastMessage(error?.message, "error");
        }
    }

    useEffect(() => {
        (async () => {
            try {
                const { data: _response } = await getUserMasterSettingsApi('consultantMasterSettings');
                if (_response?.success) {
                    setMaster({
                        isSessionHelperInfoActive: _response?.data?.data?.isSessionHelperInfoActive ?? true,
                        consultantOnlineTimeTarget: _response?.data?.data?.consultantOnlineTimeTarget ?? 5
                    })
                }
            } catch (error) {
                console.error('Error fetching consultant master settings:', error);
            }
        })()
    }, []);

    return <div className="card shadow-sm">
        <div className="card-body">
            <h5 className="card-title mb-3">Consultant Master Settings</h5>
            <div className="mb-2 d-flex align-items-center justify-content-between">
                <span>Is Session Helper Info Active:</span>
                <span className={`badge ${master.isSessionHelperInfoActive ? 'bg-success' : 'bg-danger'}`}>
                    {master.isSessionHelperInfoActive ? 'true' : 'false'}
                </span>
            </div>
            <div className="mb-2 d-flex align-items-center justify-content-between">
                <span>Consultant Online Time Target:</span>
                <span className="badge bg-info">
                    {master.consultantOnlineTimeTarget} hours/day
                </span>
            </div>
          
            <div className="mb-2 d-flex align-items-center justify-content-between">
                <label className="form-label mb-0">Session Helper Info Active</label>
                <Switch
                    checked={master.isSessionHelperInfoActive}
                    onChange={() => {
                        const email = localStorage.getItem("vedic_email") || "Admin";
                        const from = !!master.isSessionHelperInfoActive;
                        setMaster(_e => {
                            if (_e.isSessionHelperInfoActive) {
                                return {
                                    ..._e,
                                    isSessionHelperInfoActive: false
                                }
                            }
                            return {
                                ..._e,
                                isSessionHelperInfoActive: !_e.isSessionHelperInfoActive
                            }
                        })
                        safeTrack({
                            action: "TOGGLE",
                            description: `${email} toggled "Session Helper Info Active" (Consultant Master Settings)`,
                            status: "success",
                            responseStatus: 200,
                            metadata: {
                                document: "consultantMasterSettings",
                                control: "isSessionHelperInfoActive",
                                from,
                                to: !from,
                            },
                        });
                    }}
                />
            </div>
            <div className="mb-2">
                <label className="form-label mb-2">Consultant Online Time Target (hours per day)</label>
                <TextField
                    type="number"
                    size="small"
                    value={master.consultantOnlineTimeTarget}
                    onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        setMaster({
                            ...master,
                            consultantOnlineTimeTarget: value >= 0 ? value : 0
                        });
                    }}
                    inputProps={{ min: 0, step: 0.5 }}
                    fullWidth
                    helperText="Target hours per day for consultant online time"
                />
            </div>
            <div className="mb-3">
                <button
                    className="btn w-100"
                    style={SETTINGS_BUTTON_STYLE}
                    onClick={updateMaster}
                >
                    Update
                </button>
            </div>
        </div>
    </div>
}

export const UserMasterAppSettingsToggler = () => {
    const [userMaster, setUserMaster] = useState({
        isGstInclusive: true,
        supportPhoneNumber: '+91 9876543210'
    });

    const updateUserMaster = async () => {
        try {
            const email = localStorage.getItem("vedic_email") || "Admin";
            const payload = {
                isGstInclusive: userMaster.isGstInclusive,
                supportPhoneNumber: userMaster.supportPhoneNumber
            };
            const response = await updateUserMasterSettingsApi('userMasterSettings', payload);
            await safeTrack({
                action: "UPDATE",
                description: `${email} clicked "Update" (User Master App Settings)`,
                status: response?.success ? "success" : "failed",
                responseStatus: response?.code || (response?.success ? 200 : 400),
                metadata: {
                    document: "userMasterSettings",
                    payload,
                    apiResponse: response,
                },
            });
            toastMessage(response?.message || 'Successfully updated', "success");
        } catch (error) {
            const email = localStorage.getItem("vedic_email") || "Admin";
            await safeTrack({
                action: "UPDATE",
                description: `${email} clicked "Update" (User Master App Settings)`,
                status: "failed",
                responseStatus: 500,
                metadata: {
                    document: "userMasterSettings",
                    payload: {
                        isGstInclusive: userMaster.isGstInclusive,
                        supportPhoneNumber: userMaster.supportPhoneNumber
                    },
                    error: String(error?.message || error),
                },
            });
            toastMessage(error?.message, "error");
        }
    }

    useEffect(() => {
        (async () => {
            try {
                const { data: _response } = await getUserMasterSettingsApi('userMasterSettings');
                if (_response?.success) {
                    setUserMaster({
                        isGstInclusive: _response?.data?.data?.isGstInclusive,
                        supportPhoneNumber: _response?.data?.data?.supportPhoneNumber
                    })
                }
            } catch (error) {
                console.error('Error fetching user master settings:', error);
            }
        })()
    }, []);

    return <div className="card shadow-sm">
        <div className="card-body">
            <h5 className="card-title mb-3">User Master App Settings</h5>
            <div className="mb-2 d-flex align-items-center justify-content-between">
                <span>Is GST Inclusive:</span>
                <span className={`badge ${userMaster.isGstInclusive ? 'bg-success' : 'bg-danger'}`}>
                    {userMaster.isGstInclusive ? 'true' : 'false'}
                </span>
            </div>
            <div className="mb-2 d-flex align-items-center justify-content-between">
                <span>Support Phone Number:</span>
                <span className={`badge ${userMaster.supportPhoneNumber ? 'bg-success' : 'bg-danger'}`}>
                    {userMaster.supportPhoneNumber}
                </span>
            </div>
         
            <div className="mb-2 d-flex align-items-center justify-content-between">
                <label className="form-label mb-0">GST Inclusive</label>
                <Switch
                    checked={userMaster.isGstInclusive}
                    onChange={() => {
                        const email = localStorage.getItem("vedic_email") || "Admin";
                        const from = !!userMaster.isGstInclusive;
                        setUserMaster(_e => {
                            if (_e.isGstInclusive) {
                                return {
                                    ..._e,
                                    isGstInclusive: false
                                }
                            }
                            return {
                                ..._e,
                                isGstInclusive: !_e.isGstInclusive
                            }
                        })
                        safeTrack({
                            action: "TOGGLE",
                            description: `${email} toggled "GST Inclusive" (User Master App Settings)`,
                            status: "success",
                            responseStatus: 200,
                            metadata: {
                                document: "userMasterSettings",
                                control: "isGstInclusive",
                                from,
                                to: !from,
                            },
                        });
                    }}
                />
            </div>
            <div className="mb-2 d-flex align-items-center justify-content-between">
                <TextField
                    label="Support Phone Number"    
                    value={userMaster.supportPhoneNumber}
                    onChange={(e) => setUserMaster({ ...userMaster, supportPhoneNumber: e.target.value })}
                    fullWidth
                />
            </div>
            <div className="mb-3">
                <button
                    className="btn w-100"
                    style={SETTINGS_BUTTON_STYLE}
                    onClick={updateUserMaster}
                >
                    Update
                </button>
            </div>
        </div>
    </div>
}

export const KPITargetSettingsToggler = () => {
    const [kpiTargets, setKpiTargets] = useState({
        // Missed Opportunities (lower is better)
        missedOpportunities: {
            good: 0,      // 0 = good (green)
            average: 5,   // <= 5 = average (orange)
            poor: 6       // > 5 = poor (red)
        },
        // Paid Conversion (higher is better)
        paidConversion: {
            good: 35,     // >= 35% = good (green)
            average: 20,  // >= 20% = average (orange)
            poor: 0       // < 20% = poor (red)
        },
        // Online Time (percentage of target)
        onlineTime: {
            good: 80,     // >= 80% of target = good (green)
            average: 50,  // >= 50% of target = average (orange)
            poor: 0       // < 50% of target = poor (red)
        }
    });

    const updateKPITargets = async () => {
        try {
            const email = localStorage.getItem("vedic_email") || "Admin";
            const payload = {
                missedOpportunities: kpiTargets.missedOpportunities,
                paidConversion: kpiTargets.paidConversion,
                onlineTime: kpiTargets.onlineTime
            };
            const response = await updateUserMasterSettingsApi('kpiTargetSettings', payload);
            await safeTrack({
                action: "UPDATE",
                description: `${email} clicked "Update KPI Targets" (KPI Target Settings)`,
                status: response?.success ? "success" : "failed",
                responseStatus: response?.code || (response?.success ? 200 : 400),
                metadata: {
                    document: "kpiTargetSettings",
                    payload,
                    apiResponse: response,
                },
            });
            toastMessage(response?.message || 'Successfully updated KPI targets', "success");
        } catch (error) {
            const email = localStorage.getItem("vedic_email") || "Admin";
            await safeTrack({
                action: "UPDATE",
                description: `${email} clicked "Update KPI Targets" (KPI Target Settings)`,
                status: "failed",
                responseStatus: 500,
                metadata: {
                    document: "kpiTargetSettings",
                    payload: {
                        missedOpportunities: kpiTargets.missedOpportunities,
                        paidConversion: kpiTargets.paidConversion,
                        onlineTime: kpiTargets.onlineTime
                    },
                    error: String(error?.message || error),
                },
            });
            toastMessage(error?.message, "error");
        }
    }

    useEffect(() => {
        (async () => {
            try {
                const { data: _response } = await getUserMasterSettingsApi('kpiTargetSettings');
                if (_response?.success && _response?.data?.data) {
                    setKpiTargets({
                        missedOpportunities: _response?.data?.data?.missedOpportunities || kpiTargets.missedOpportunities,
                        paidConversion: _response?.data?.data?.paidConversion || kpiTargets.paidConversion,
                        onlineTime: _response?.data?.data?.onlineTime || kpiTargets.onlineTime
                    });
                }
            } catch (error) {
                console.error('Error fetching KPI target settings:', error);
            }
        })()
    }, []);

    return (
        <div className="card shadow-sm">
            <div className="card-body">
                <h5 className="card-title mb-3">KPI Target Settings</h5>
                <p className="text-muted mb-3">Configure thresholds for Green (Good), Orange (Average), and Red (Poor) status indicators</p>
                
                {/* Missed Opportunities */}
                <div className="mb-4 p-3 border rounded">
                    <h6 className="mb-3">Missed Opportunities (Lower is Better)</h6>
                    <div className="mb-2">
                        <label className="form-label">Good (Green): &lt;= </label>
                        <TextField
                            type="number"
                            size="small"
                            value={kpiTargets.missedOpportunities.good}
                            onChange={(e) => setKpiTargets({
                                ...kpiTargets,
                                missedOpportunities: {
                                    ...kpiTargets.missedOpportunities,
                                    good: parseInt(e.target.value) || 0
                                }
                            })}
                            fullWidth
                        />
                    </div>
                    <div className="mb-2">
                        <label className="form-label">Average (Orange): &lt;= </label>
                        <TextField
                            type="number"
                            size="small"
                            value={kpiTargets.missedOpportunities.average}
                            onChange={(e) => setKpiTargets({
                                ...kpiTargets,
                                missedOpportunities: {
                                    ...kpiTargets.missedOpportunities,
                                    average: parseInt(e.target.value) || 0
                                }
                            })}
                            fullWidth
                        />
                    </div>
                    <div className="mb-2">
                        <label className="form-label">Poor (Red): &gt; </label>
                        <TextField
                            type="number"
                            size="small"
                            value={kpiTargets.missedOpportunities.poor}
                            onChange={(e) => setKpiTargets({
                                ...kpiTargets,
                                missedOpportunities: {
                                    ...kpiTargets.missedOpportunities,
                                    poor: parseInt(e.target.value) || 0
                                }
                            })}
                            fullWidth
                        />
                    </div>
                </div>

                {/* Paid Conversion */}
                <div className="mb-4 p-3 border rounded">
                    <h6 className="mb-3">Paid Conversion % (Higher is Better)</h6>
                    <div className="mb-2">
                        <label className="form-label">Good (Green): &gt;= </label>
                        <TextField
                            type="number"
                            size="small"
                            value={kpiTargets.paidConversion.good}
                            onChange={(e) => setKpiTargets({
                                ...kpiTargets,
                                paidConversion: {
                                    ...kpiTargets.paidConversion,
                                    good: parseInt(e.target.value) || 0
                                }
                            })}
                            fullWidth
                        />
                    </div>
                    <div className="mb-2">
                        <label className="form-label">Average (Orange): &gt;= </label>
                        <TextField
                            type="number"
                            size="small"
                            value={kpiTargets.paidConversion.average}
                            onChange={(e) => setKpiTargets({
                                ...kpiTargets,
                                paidConversion: {
                                    ...kpiTargets.paidConversion,
                                    average: parseInt(e.target.value) || 0
                                }
                            })}
                            fullWidth
                        />
                    </div>
                    <div className="mb-2">
                        <label className="form-label">Poor (Red): &lt; </label>
                        <TextField
                            type="number"
                            size="small"
                            value={kpiTargets.paidConversion.poor}
                            onChange={(e) => setKpiTargets({
                                ...kpiTargets,
                                paidConversion: {
                                    ...kpiTargets.paidConversion,
                                    poor: parseInt(e.target.value) || 0
                                }
                            })}
                            fullWidth
                        />
                    </div>
                </div>

                {/* Online Time */}
                <div className="mb-4 p-3 border rounded">
                    <h6 className="mb-3">Online Time % of Target (Higher is Better)</h6>
                    <div className="mb-2">
                        <label className="form-label">Good (Green): &gt;= </label>
                        <TextField
                            type="number"
                            size="small"
                            value={kpiTargets.onlineTime.good}
                            onChange={(e) => setKpiTargets({
                                ...kpiTargets,
                                onlineTime: {
                                    ...kpiTargets.onlineTime,
                                    good: parseInt(e.target.value) || 0
                                }
                            })}
                            fullWidth
                        />
                        <small className="text-muted">% of target hours</small>
                    </div>
                    <div className="mb-2">
                        <label className="form-label">Average (Orange): &gt;= </label>
                        <TextField
                            type="number"
                            size="small"
                            value={kpiTargets.onlineTime.average}
                            onChange={(e) => setKpiTargets({
                                ...kpiTargets,
                                onlineTime: {
                                    ...kpiTargets.onlineTime,
                                    average: parseInt(e.target.value) || 0
                                }
                            })}
                            fullWidth
                        />
                        <small className="text-muted">% of target hours</small>
                    </div>
                    <div className="mb-2">
                        <label className="form-label">Poor (Red): &lt; </label>
                        <TextField
                            type="number"
                            size="small"
                            value={kpiTargets.onlineTime.poor}
                            onChange={(e) => setKpiTargets({
                                ...kpiTargets,
                                onlineTime: {
                                    ...kpiTargets.onlineTime,
                                    poor: parseInt(e.target.value) || 0
                                }
                            })}
                            fullWidth
                        />
                        <small className="text-muted">% of target hours</small>
                    </div>
                </div>

                <div className="mb-3">
                    <button
                        className="btn w-100"
                        style={SETTINGS_BUTTON_STYLE}
                        onClick={updateKPITargets}
                    >
                        Update KPI Targets
                    </button>
                </div>
            </div>
        </div>
    );
}

const createDefaultSessionItem = () => ({
    price: 0,
    priceOffer: 0,
    description: '',
    consultationTimeInMinutes: 60,
    title: '',
    image: '',
    status: true,
});

const slugifyKey = (label) => {
    const base = String(label || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    return base || `category_${Date.now()}`;
};

const defaultFixedSessionData = {
    fixed_banners: [{ bannerImage: '', status: true }],
    specialist: [createDefaultSessionItem()],
    careerAndGrowth: [createDefaultSessionItem()],
    categoriesMeta: [
        { key: 'specialist', label: 'Specialist', status: true },
        { key: 'careerAndGrowth', label: 'Career And Growth', status: true },
    ],
    AFTER_CONSULTATION_FREE_FOLLOW_UP: 'After Consultation : 7 Days Free Follow Up*',
    learnHowToBookFixedSessionUrl: '',
};

export const FixedSessionSettingsToggler = () => {
    const [form, setForm] = useState(defaultFixedSessionData);
    const [saving, setSaving] = useState(false);
    const [uploadingKey, setUploadingKey] = useState('');
    const [editModal, setEditModal] = useState({
        open: false,
        type: 'category', // 'banner' | 'category'
        categoryKey: '',
        index: -1,
        draft: null,
    });
    const [categoryModal, setCategoryModal] = useState({ open: false, label: '' });
    const [deleteCategoryModal, setDeleteCategoryModal] = useState({ open: false, key: '', label: '' });
    const didInitialLoad = useRef(false);
    const autoSaveTimerRef = useRef(null);
    const logFixedAction = (description, metadata = {}) => {
        safeTrack({
            action: 'UPDATE',
            module: 'Settings',
            description: `Fixed Session: ${description}`,
            metadata: { section: 'fixed_session', ...metadata },
            status: 'success',
            responseStatus: 200,
        });
    };

    const setRootField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const setBannerField = (index, field, value) => {
        setForm((prev) => {
            const next = [...(prev.fixed_banners || [])];
            next[index] = { ...(next[index] || { bannerImage: '', status: true }), [field]: value };
            return { ...prev, fixed_banners: next };
        });
    };

    const setCategoryField = (category, index, field, value) => {
        setForm((prev) => {
            const next = [...(prev[category] || [])];
            next[index] = { ...(next[index] || createDefaultSessionItem()), [field]: value };
            return { ...prev, [category]: next };
        });
    };

    const setCategoryMeta = (key, patch) => {
        setForm((prev) => ({
            ...prev,
            categoriesMeta: (prev.categoriesMeta || []).map((c) => (c.key === key ? { ...c, ...patch } : c)),
        }));
    };

    const addBanner = () => {
        setForm((prev) => ({
            ...prev,
            fixed_banners: [...(prev.fixed_banners || []), { bannerImage: '', status: true }],
        }));
        logFixedAction('Created banner row');
    };

    const removeBanner = (index) => {
        setForm((prev) => ({
            ...prev,
            fixed_banners: (prev.fixed_banners || []).filter((_, idx) => idx !== index),
        }));
        logFixedAction('Deleted banner row', { index });
    };

    const addCategoryItem = (category) => {
        setForm((prev) => ({
            ...prev,
            [category]: [...(prev[category] || []), createDefaultSessionItem()],
        }));
        logFixedAction('Created category row', { category });
    };

    const removeCategoryItem = (category, index) => {
        setForm((prev) => ({
            ...prev,
            [category]: (prev[category] || []).filter((_, idx) => idx !== index),
        }));
        logFixedAction('Deleted category row', { category, index });
    };

    const normalizeSessionArray = (arr) => (arr || []).map((item) => ({
        price: Number(item?.price || 0),
        priceOffer: Number(item?.priceOffer || 0),
        description: item?.description || '',
        consultationTimeInMinutes: Number(item?.consultationTimeInMinutes || 0),
        title: item?.title || '',
        image: item?.image || '',
        status: item?.status !== false,
    }));

    const normalizePayload = () => {
        const payload = {
            fixed_banners: (form.fixed_banners || []).map((item) => ({
                bannerImage: item?.bannerImage || '',
                status: item?.status !== false,
            })),
            categoriesMeta: (form.categoriesMeta || []).map((c) => ({
                key: c.key,
                label: c.label || c.key,
                status: c.status !== false,
            })),
            AFTER_CONSULTATION_FREE_FOLLOW_UP: form.AFTER_CONSULTATION_FREE_FOLLOW_UP || '',
            learnHowToBookFixedSessionUrl: form.learnHowToBookFixedSessionUrl || '',
        };

        (form.categoriesMeta || []).forEach((c) => {
            payload[c.key] = normalizeSessionArray(form[c.key] || []);
        });

        // Backward compatibility if meta missing but arrays exist
        if (!payload.specialist && Array.isArray(form.specialist)) payload.specialist = normalizeSessionArray(form.specialist);
        if (!payload.careerAndGrowth && Array.isArray(form.careerAndGrowth)) payload.careerAndGrowth = normalizeSessionArray(form.careerAndGrowth);

        return payload;
    };

    const inferCategoriesMeta = (data) => {
        const meta = [];
        const add = (key, label) => {
            if (meta.some((m) => m.key === key)) return;
            meta.push({ key, label: label || key, status: true });
        };
        add('specialist', 'Specialist');
        add('careerAndGrowth', 'Career And Growth');

        Object.keys(data || {}).forEach((k) => {
            if (['fixed_banners', 'AFTER_CONSULTATION_FREE_FOLLOW_UP', 'learnHowToBookFixedSessionUrl', 'categoriesMeta'].includes(k)) return;
            if (Array.isArray(data[k]) && data[k].length >= 0) {
                // heuristic: array of objects with title/price etc
                const first = data[k][0];
                if (first && typeof first === 'object' && ('title' in first || 'price' in first || 'consultationTimeInMinutes' in first)) {
                    add(k, k);
                }
            }
        });
        return meta;
    };

    const uploadImage = async (file, onSuccess, key) => {
        if (!file) return;
        const formData = new FormData();
        formData.append('image', file);
        setUploadingKey(key);
        try {
            const response = await uploadFixedSessionImageApi(formData);
            if (!response?.success || !response?.data?.url) {
                throw new Error(response?.message || 'Failed to upload image');
            }
            onSuccess(response.data.url);
            toastMessage('Image uploaded successfully', 'success');
        } catch (error) {
            toastMessage(error?.message || 'Image upload failed', 'error');
        } finally {
            setUploadingKey('');
        }
    };

    const loadData = async () => {
        try {
            const response = await getUserMasterSettingsApi('fixed_session');
            if (response?.success && response?.data?.data) {
                const incoming = response.data.data;
                const meta = Array.isArray(incoming.categoriesMeta) && incoming.categoriesMeta.length > 0
                    ? incoming.categoriesMeta
                    : inferCategoriesMeta(incoming);
                setForm((prev) => ({
                    ...prev,
                    ...incoming,
                    fixed_banners: incoming.fixed_banners || prev.fixed_banners,
                    categoriesMeta: meta,
                    ...meta.reduce((acc, c) => {
                        acc[c.key] = incoming[c.key] || prev[c.key] || [];
                        return acc;
                    }, {}),
                }));
            }
        } catch (error) {
            toastMessage(error?.message || 'Failed to fetch fixed session settings', 'error');
        } finally {
            didInitialLoad.current = true;
        }
    };

    const openBannerEdit = (index) => {
        const row = (form.fixed_banners || [])[index] || { bannerImage: '', status: true };
        setEditModal({ open: true, type: 'banner', categoryKey: '', index, draft: { ...row } });
    };

    const openCategoryEdit = (categoryKey, index) => {
        const row = (form[categoryKey] || [])[index] || createDefaultSessionItem();
        setEditModal({ open: true, type: 'category', categoryKey, index, draft: { ...row } });
    };

    const closeEdit = () => setEditModal((m) => ({ ...m, open: false, draft: null }));

    const applyEdit = () => {
        if (!editModal.draft) return;
        if (editModal.type === 'banner') {
            setForm((prev) => {
                const next = [...(prev.fixed_banners || [])];
                next[editModal.index] = { ...(next[editModal.index] || {}), ...editModal.draft };
                return { ...prev, fixed_banners: next };
            });
        } else {
            setForm((prev) => {
                const next = [...(prev[editModal.categoryKey] || [])];
                next[editModal.index] = { ...(next[editModal.index] || {}), ...editModal.draft };
                return { ...prev, [editModal.categoryKey]: next };
            });
        }
        logFixedAction('Edited row', {
            type: editModal.type,
            categoryKey: editModal.categoryKey || 'fixed_banners',
            index: editModal.index,
        });
        closeEdit();
    };

    const openAddCategory = () => setCategoryModal({ open: true, label: '' });
    const confirmAddCategory = () => {
        const label = String(categoryModal.label || '').trim();
        if (!label) {
            toastMessage('Category name is required', 'error');
            return;
        }
        const keyBase = slugifyKey(label);
        setForm((prev) => {
            const existingKeys = new Set((prev.categoriesMeta || []).map((c) => c.key));
            let key = keyBase;
            let i = 1;
            while (existingKeys.has(key)) {
                key = `${keyBase}_${i++}`;
            }
            return {
                ...prev,
                categoriesMeta: [...(prev.categoriesMeta || []), { key, label, status: true }],
                [key]: [createDefaultSessionItem()],
            };
        });
        logFixedAction('Created category', { label });
        setCategoryModal({ open: false, label: '' });
    };

    const requestDeleteCategory = (key, label) => {
        setDeleteCategoryModal({ open: true, key, label: label || key });
    };

    const confirmDeleteCategory = () => {
        const { key } = deleteCategoryModal;
        if (!key) return;
        setForm((prev) => {
            const next = { ...prev };
            next.categoriesMeta = (prev.categoriesMeta || []).filter((c) => c.key !== key);
            delete next[key];
            return next;
        });
        logFixedAction('Deleted category', { key });
        setDeleteCategoryModal({ open: false, key: '', label: '' });
    };

    useEffect(() => {
        loadData();
    }, []);

    // Auto-save (debounced) so refresh doesn't lose unsaved edits
    useEffect(() => {
        if (!didInitialLoad.current) return;
        if (uploadingKey) return;

        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = setTimeout(async () => {
            const payload = normalizePayload();
            setSaving(true);
            try {
                const response = await updateUserMasterSettingsApi('fixed_session', payload);
                if (!response?.success) throw new Error(response?.message || 'Failed to save settings');
            } catch (error) {
                toastMessage(error?.message || 'Failed to auto-save fixed session settings', 'error');
            } finally {
                setSaving(false);
            }
        }, 800);

        return () => {
            if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, uploadingKey]);

    const saveData = async () => {
        const payload = normalizePayload();
        setSaving(true);
        try {
            const response = await updateUserMasterSettingsApi('fixed_session', payload);
            if (!response?.success) {
                throw new Error(response?.message || 'Failed to save settings');
            }
            toastMessage(response?.message || 'Fixed session settings updated', 'success');
            logFixedAction('Saved fixed session settings');
        } catch (error) {
            toastMessage(error?.message || 'Failed to save fixed session settings', 'error');
        } finally {
            setSaving(false);
        }
    };

    const renderSessionList = (categoryKey) => (
        <div className="mb-4">
            <div className="table-responsive" style={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
                <table className="table table-bordered align-middle mb-0" style={{ width: '100%', minWidth: '100%', margin: 0, borderColor: SETTINGS_TABLE_BORDER, tableLayout: 'fixed' }}>
                    <thead>
                        <tr>
                            <th style={{ width: 70 }}>S. No.</th>
                            <th style={{ width: 110 }}>Status</th>
                            <th>Title</th>
                            <th style={{ width: 110 }}>Price</th>
                            <th style={{ width: 120 }}>Offer</th>
                            <th style={{ width: 130 }}>Minutes</th>
                            <th style={{ width: 120 }}>Image</th>
                            <th style={{ width: 120 }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(form[categoryKey] || []).length === 0 ? (
                            <tr>
                                <td colSpan={8} className="text-center text-muted py-4">
                                    No rows added
                                </td>
                            </tr>
                        ) : (
                            (form[categoryKey] || []).map((item, index) => (
                                <tr key={`${categoryKey}-${index}`}>
                                    <td>{index + 1}</td>
                                    <td>
                                        <Switch
                                            checked={item?.status !== false}
                                            onChange={() => setCategoryField(categoryKey, index, 'status', !(item?.status !== false))}
                                        />
                                    </td>
                                    <td>{item?.title ? item.title : BLANK}</td>
                                    <td>{Number(item?.price || 0)}</td>
                                    <td>{Number(item?.priceOffer || 0)}</td>
                                    <td>{Number(item?.consultationTimeInMinutes || 0)}</td>
                                    <td>
                                        {item?.image ? (
                                            <img
                                                src={item.image}
                                                alt=""
                                                style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover', border: '1px solid #e5e7eb' }}
                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                            />
                                        ) : BLANK}
                                    </td>
                                    <td>
                                        <IconButton size="small" onClick={() => openCategoryEdit(categoryKey, index)} title="Edit">
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => removeCategoryItem(categoryKey, index)} title="Delete">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div className="card shadow-sm">
            <div className="card-body" style={{ maxWidth: '100%', overflowX: 'hidden', padding: 0 }}>
                <div style={{ padding: 16, width: '100%' }}>
                    <h5 className="card-title mb-2">Fixed Session Settings DTO</h5>
                 
                </div>

                <div className="mb-4" style={{ padding: '0 8px', marginBottom: 24 }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">Fixed Banners</h6>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                addBanner();
                                const idx = (form.fixed_banners || []).length;
                                setTimeout(() => openBannerEdit(idx), 0);
                            }}
                        >
                            Add
                        </Button>
                    </div>
                    <div className="table-responsive" style={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
                        <table className="table table-bordered align-middle mb-0" style={{ width: '100%', minWidth: '100%', margin: 0, borderColor: SETTINGS_TABLE_BORDER, tableLayout: 'fixed' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 70 }}>S. No.</th>
                                    <th style={{ width: 110 }}>Status</th>
                                    <th style={{ width: 140 }}>Banner</th>
                                    <th>URL</th>
                                    <th style={{ width: 120 }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(form.fixed_banners || []).length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="text-center text-muted py-4">
                                            No banners added
                                        </td>
                                    </tr>
                                ) : (
                                    (form.fixed_banners || []).map((banner, index) => (
                                        <tr key={`banner-${index}`}>
                                            <td>{index + 1}</td>
                                            <td>
                                                <Switch
                                                    checked={banner?.status !== false}
                                                    onChange={() => setBannerField(index, 'status', !(banner?.status !== false))}
                                                />
                                            </td>
                                            <td>
                                                {banner?.bannerImage ? (
                                                    <img
                                                        src={banner.bannerImage}
                                                        alt=""
                                                        style={{ width: 56, height: 32, borderRadius: 8, objectFit: 'cover', border: '1px solid #e5e7eb' }}
                                                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                    />
                                                ) : BLANK}
                                            </td>
                                            <td style={{ maxWidth: 520, wordBreak: 'break-all' }}>
                                                {banner?.bannerImage ? banner.bannerImage : BLANK}
                                            </td>
                                            <td>
                                                <IconButton size="small" onClick={() => openBannerEdit(index)} title="Edit">
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => removeBanner(index)} title="Delete">
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3" style={{ padding: '0 8px' }}>
                    <h6 className="mb-0">Categories</h6>
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openAddCategory}>
                        Add Category
                    </Button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: '0 8px', marginBottom: 24 }}>
                    {(form.categoriesMeta || []).map((c) => (
                        <div
                            key={c.key}
                            style={{ padding: 0, background: 'transparent' }}
                        >
                            <div className="d-flex justify-content-between align-items-center mb-2" style={{ gap: 12 }}>
                                <div className="d-flex align-items-center" style={{ gap: 10, flexWrap: 'wrap' }}>
                                    <strong>{c.label || c.key}</strong>
                                </div>
                                <div className="d-flex align-items-center" style={{ gap: 10 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={() => {
                                            addCategoryItem(c.key);
                                            const idx = (form[c.key] || []).length;
                                            setTimeout(() => openCategoryEdit(c.key, idx), 0);
                                        }}
                                    >
                                        ADD
                                    </Button>
                                    <IconButton
                                        size="small"
                                        onClick={() => requestDeleteCategory(c.key, c.label)}
                                        title="Delete Category"
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                    <span className="text-muted" style={{ fontSize: 12 }}>Category ON/OFF</span>
                                    <Switch
                                        checked={c.status !== false}
                                        onChange={() => setCategoryMeta(c.key, { status: !(c.status !== false) })}
                                    />
                                </div>
                            </div>
                            {renderSessionList(c.key)}
                        </div>
                    ))}
                </div>

                <Dialog open={categoryModal.open} onClose={() => setCategoryModal({ open: false, label: '' })} maxWidth="xs" fullWidth>
                    <DialogTitle>Add Category</DialogTitle>
                    <DialogContent dividers>
                        <TextField
                            label="Category name"
                            value={categoryModal.label}
                            onChange={(e) => setCategoryModal((m) => ({ ...m, label: e.target.value }))}
                            fullWidth
                            autoFocus
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setCategoryModal({ open: false, label: '' })} color="inherit">Cancel</Button>
                        <Button onClick={confirmAddCategory} variant="contained">Create</Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={deleteCategoryModal.open} onClose={() => setDeleteCategoryModal({ open: false, key: '', label: '' })} maxWidth="xs" fullWidth>
                    <DialogTitle>Delete Category</DialogTitle>
                    <DialogContent dividers>
                        Are you sure you want to delete <strong>{deleteCategoryModal.label}</strong>?
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteCategoryModal({ open: false, key: '', label: '' })} color="inherit">Cancel</Button>
                        <Button onClick={confirmDeleteCategory} color="error" variant="contained">Delete</Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={editModal.open} onClose={closeEdit} maxWidth="sm" fullWidth>
                    <DialogTitle>{editModal.type === 'banner' ? 'Edit Banner' : 'Edit Item'}</DialogTitle>
                    <DialogContent dividers>
                        {editModal.type === 'banner' ? (
                            <>
                                {!!editModal.draft?.bannerImage && (
                                    <div className="mb-3">
                                        <TextField
                                            label="Banner Image URL"
                                            value={editModal.draft?.bannerImage || ''}
                                            onChange={(e) => setEditModal((m) => ({ ...m, draft: { ...(m.draft || {}), bannerImage: e.target.value } }))}
                                            fullWidth
                                        />
                                    </div>
                                )}
                                <div className="mb-2">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="form-control"
                                        onChange={(e) => uploadImage(
                                            e.target.files?.[0],
                                            (url) => setEditModal((m) => ({ ...m, draft: { ...(m.draft || {}), bannerImage: url } })),
                                            `banner-modal-${editModal.index}`
                                        )}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="row g-2">
                                    <div className="col-12">
                                        <TextField
                                            label="Title"
                                            value={editModal.draft?.title || ''}
                                            onChange={(e) => setEditModal((m) => ({ ...m, draft: { ...(m.draft || {}), title: e.target.value } }))}
                                            fullWidth
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <TextField
                                            label="Price"
                                            type="number"
                                            value={editModal.draft?.price ?? 0}
                                            onChange={(e) => setEditModal((m) => ({ ...m, draft: { ...(m.draft || {}), price: e.target.value } }))}
                                            fullWidth
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <TextField
                                            label="Offer Price"
                                            type="number"
                                            value={editModal.draft?.priceOffer ?? 0}
                                            onChange={(e) => setEditModal((m) => ({ ...m, draft: { ...(m.draft || {}), priceOffer: e.target.value } }))}
                                            fullWidth
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <TextField
                                            label="Consultation Minutes"
                                            type="number"
                                            value={editModal.draft?.consultationTimeInMinutes ?? 0}
                                            onChange={(e) => setEditModal((m) => ({ ...m, draft: { ...(m.draft || {}), consultationTimeInMinutes: e.target.value } }))}
                                            fullWidth
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        {!!editModal.draft?.image && (
                                            <TextField
                                                label="Image URL"
                                                value={editModal.draft?.image || ''}
                                                onChange={(e) => setEditModal((m) => ({ ...m, draft: { ...(m.draft || {}), image: e.target.value } }))}
                                                fullWidth
                                            />
                                        )}
                                    </div>
                                    <div className="col-12">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="form-control"
                                            onChange={(e) => uploadImage(
                                                e.target.files?.[0],
                                                (url) => setEditModal((m) => ({ ...m, draft: { ...(m.draft || {}), image: url } })),
                                                `item-modal-${editModal.categoryKey}-${editModal.index}`
                                            )}
                                        />
                                    </div>
                                    <div className="col-12">
                                        <TextField
                                            label="Description"
                                            value={editModal.draft?.description || ''}
                                            onChange={(e) => setEditModal((m) => ({ ...m, draft: { ...(m.draft || {}), description: e.target.value } }))}
                                            fullWidth
                                            multiline
                                            minRows={3}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeEdit} color="inherit">Cancel</Button>
                        <Button onClick={applyEdit} variant="contained">Save</Button>
                    </DialogActions>
                </Dialog>

                <div className="mb-3" style={{ padding: '0 8px' }}>
                    <TextField
                        label="After Consultation Free Follow Up"
                        value={form.AFTER_CONSULTATION_FREE_FOLLOW_UP || ''}
                        onChange={(e) => setRootField('AFTER_CONSULTATION_FREE_FOLLOW_UP', e.target.value)}
                        fullWidth
                    />
                </div>
                <div className="mb-4" style={{ padding: '0 8px' }}>
                    <TextField
                        label="Learn How To Book Fixed Session URL"
                        value={form.learnHowToBookFixedSessionUrl || ''}
                        onChange={(e) => setRootField('learnHowToBookFixedSessionUrl', e.target.value)}
                        fullWidth
                    />
                </div>

                <div style={{ padding: '0 8px 28px' }}>
                    <button type="button" className="btn w-100" style={SETTINGS_BUTTON_STYLE} onClick={saveData} disabled={saving || !!uploadingKey}>
                        {saving ? 'Saving...' : 'Save Fixed Session DTO'}
                    </button>
                    {!!uploadingKey && (
                        <div className="d-flex align-items-center gap-2 mt-2 text-muted">
                            <CircularProgress size={16} />
                            <small>Uploading image...</small>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const S3_MEDIA_BASE = 'https://vedic-meet-bucket.s3.ap-south-1.amazonaws.com/';

function normalizeMaybeUrl(value) {
    const v = String(value || '').trim();
    if (!v) return '';
    if (/^https?:\/\//i.test(v)) return v;
    return `${S3_MEDIA_BASE}${v}`;
}

export const MenuSettingsToggler = () => {
    const [rows, setRows] = useState([]);
    const [saving, setSaving] = useState(false);
    const [modal, setModal] = useState({ open: false, index: -1, draft: null });
    const [uploading, setUploading] = useState(false);
    const didLoadMenu = useRef(false);
    const menuAutoSaveTimer = useRef(null);
    const logMenuAction = (description, metadata = {}) => {
        safeTrack({
            action: 'UPDATE',
            module: 'Settings',
            description: `Menu Settings: ${description}`,
            metadata: { section: 'Menu', ...metadata },
            status: 'success',
            responseStatus: 200,
        });
    };

    const load = async () => {
        try {
            const response = await getUserMasterSettingsApi('Menu');
            const actualDoc = response?.data;
            const actualData = actualDoc?.data?.data ?? actualDoc?.data ?? null;
            const menuDataObj = actualData && typeof actualData === 'object' && !Array.isArray(actualData) ? actualData : {};

            const list = Object.entries(menuDataObj).map(([name, v]) => ({
                name,
                icon: v?.icon || '',
                Content: v?.Content || '',
                screen: v?.screen || '',
                status: v?.status !== false, // optional support
            }));
            setRows(list);
        } catch (_e) {
            setRows([]);
        } finally {
            didLoadMenu.current = true;
        }
    };

    useEffect(() => { load(); }, []);

    const openEdit = (index) => {
        const row = rows[index] || { name: '', icon: '', Content: '', screen: '', status: true };
        setModal({ open: true, index, draft: { ...row } });
    };

    const addNew = () => {
        setRows((prev) => {
            const next = [...prev, { name: 'New Item', icon: '', Content: '', screen: '', status: true }];
            const idx = next.length - 1;
            setTimeout(() => openEdit(idx), 0);
            return next;
        });
        logMenuAction('Created menu row');
    };

    const removeRow = (index) => {
        setRows((prev) => prev.filter((_, i) => i !== index));
        logMenuAction('Deleted menu row', { index });
    };

    const uploadIcon = async (file) => {
        if (!file) return;
        const formData = new FormData();
        formData.append('image', file);
        setUploading(true);
        try {
            const res = await uploadSettingsImageApi(formData);
            if (!res?.success || !res?.data?.key) throw new Error(res?.message || 'Upload failed');
            setModal((m) => ({ ...m, draft: { ...(m.draft || {}), icon: res.data.key } }));
        } catch (e) {
            toastMessage(e?.message || 'Upload failed', 'error');
        } finally {
            setUploading(false);
        }
    };

    const save = async (opts = { silent: false, refreshAfter: true }) => {
        const obj = {};
        rows.forEach((r) => {
            if (!r?.name) return;
            obj[r.name] = {
                icon: r.icon || '',
                Content: r.Content || '',
                screen: r.screen || '',
            };
        });
        setSaving(true);
        try {
            const response = await updateUserMasterSettingsApi('Menu', obj);
            if (!response?.success) throw new Error(response?.message || 'Failed to save Menu');
            if (!opts?.silent) toastMessage(response?.message || 'Menu saved', 'success');
            if (opts?.refreshAfter) await load();
            if (!opts?.silent) logMenuAction('Saved menu settings');
        } catch (e) {
            toastMessage(e?.message || 'Failed to save Menu', 'error');
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (!didLoadMenu.current) return;
        if (uploading) return;

        if (menuAutoSaveTimer.current) clearTimeout(menuAutoSaveTimer.current);
        menuAutoSaveTimer.current = setTimeout(() => {
            save({ silent: true, refreshAfter: false });
        }, 800);

        return () => {
            if (menuAutoSaveTimer.current) clearTimeout(menuAutoSaveTimer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, uploading]);

    const applyModal = () => {
        setRows((prev) => prev.map((r, i) => (i === modal.index ? { ...r, ...modal.draft } : r)));
        logMenuAction('Edited menu row', { index: modal.index, key: modal.draft?.name || '' });
        setModal({ open: false, index: -1, draft: null });
    };

    return (
        <div className="card shadow-sm">
            <div className="card-body" style={{ padding: 0 }}>
                <div style={{ padding: 16 }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Menu Settings</h5>
                        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addNew}>Add Menu</Button>
                    </div>
                   
                </div>
                <div className="table-responsive" style={{ padding: '0 16px 16px', width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
                    <table className="table table-bordered align-middle mb-0" style={{ width: '100%', minWidth: '100%', margin: 0, borderColor: SETTINGS_TABLE_BORDER, tableLayout: 'fixed' }}>
                        <thead>
                            <tr>
                                <th style={{ width: 70 }}>S. No.</th>
                                <th>Title</th>
                                <th style={{ width: 120 }}>Icon</th>
                                <th>Content</th>
                                <th style={{ width: 180 }}>Screen</th>
                                <th style={{ width: 120 }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr><td colSpan={6} className="text-center text-muted py-4">No menu items</td></tr>
                            ) : rows.map((r, idx) => (
                                <tr key={idx}>
                                    <td>{idx + 1}</td>
                                    <td>{r.name}</td>
                                    <td>
                                        {r.icon ? (
                                            <img src={normalizeMaybeUrl(r.icon)} alt="" style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'cover', border: '1px solid #e5e7eb' }} />
                                        ) : BLANK}
                                    </td>
                                    <td style={{ maxWidth: 420 }}>{r.Content ? r.Content : BLANK}</td>
                                    <td>{r.screen ? r.screen : BLANK}</td>
                                    <td>
                                        <IconButton size="small" onClick={() => openEdit(idx)} title="Edit"><EditIcon fontSize="small" /></IconButton>
                                        <IconButton size="small" onClick={() => removeRow(idx)} title="Delete"><DeleteIcon fontSize="small" /></IconButton>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div style={{ padding: '0 16px 16px', width: '100%' }}>
                    <button type="button" className="btn w-100" style={SETTINGS_BUTTON_STYLE} onClick={save} disabled={saving}>
                        {saving ? 'Saving...' : 'Save Menu Settings'}
                    </button>
                </div>
            </div>

            <Dialog open={modal.open} onClose={() => setModal({ open: false, index: -1, draft: null })} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Menu Item</DialogTitle>
                <DialogContent dividers>
                    <div className="row g-2">
                        <div className="col-12">
                            <TextField label="Menu Title (Object Key)" value={modal.draft?.name || ''} onChange={(e) => setModal((m) => ({ ...m, draft: { ...(m.draft || {}), name: e.target.value } }))} fullWidth />
                        </div>
                        <div className="col-12">
                            <TextField label="Screen" value={modal.draft?.screen || ''} onChange={(e) => setModal((m) => ({ ...m, draft: { ...(m.draft || {}), screen: e.target.value } }))} fullWidth />
                        </div>
                        <div className="col-12">
                            <TextField label="Content" value={modal.draft?.Content || ''} onChange={(e) => setModal((m) => ({ ...m, draft: { ...(m.draft || {}), Content: e.target.value } }))} fullWidth multiline minRows={3} />
                        </div>
                        <div className="col-12">
                            <input type="file" accept="image/*" className="form-control mt-2" onChange={(e) => uploadIcon(e.target.files?.[0])} />
                            {uploading ? <small className="text-muted d-block mt-1">Uploading...</small> : null}
                            {!!modal.draft?.icon && (
                                <TextField
                                    label="Icon (S3 Key)"
                                    value={modal.draft?.icon || ''}
                                    onChange={(e) => setModal((m) => ({ ...m, draft: { ...(m.draft || {}), icon: e.target.value } }))}
                                    fullWidth
                                    className="mt-2"
                                />
                            )}
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModal({ open: false, index: -1, draft: null })} color="inherit">Cancel</Button>
                    <Button onClick={applyModal} variant="contained">Apply</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export const KnowledgeDataSettingsToggler = () => {
    const [list, setList] = useState([]);
    const [saving, setSaving] = useState(false);
    const [modal, setModal] = useState({ open: false, index: -1, draft: null });
    const [previewModal, setPreviewModal] = useState({ open: false, row: null });
    const [uploading, setUploading] = useState(false);
    const didLoadKnowledge = useRef(false);
    const knowledgeAutoSaveTimer = useRef(null);
    const logKnowledgeAction = (description, metadata = {}) => {
        safeTrack({
            action: 'UPDATE',
            module: 'Settings',
            description: `Knowledge Data: ${description}`,
            metadata: { section: 'knowledgeData', ...metadata },
            status: 'success',
            responseStatus: 200,
        });
    };

    const load = async () => {
        try {
            const response = await getUserMasterSettingsApi('knowledgeData');
            const actualDoc = response?.data;
            const actualData = actualDoc?.data?.data ?? actualDoc?.data ?? null;
            setList(Array.isArray(actualData) ? actualData : []);
        } catch {
            setList([]);
        } finally {
            didLoadKnowledge.current = true;
        }
    };
    useEffect(() => { load(); }, []);

    const openEdit = (index) => {
        const row = list[index] || { title: '', description: '', createdAt: new Date().toISOString(), bannerImage: '', content: [] };
        setModal({ open: true, index, draft: JSON.parse(JSON.stringify(row)) });
    };

    const addNew = () => {
        setList((prev) => {
            const next = [...prev, { title: 'New Article', description: '', createdAt: new Date().toISOString(), bannerImage: '', content: [] }];
            const idx = next.length - 1;
            setTimeout(() => openEdit(idx), 0);
            return next;
        });
        logKnowledgeAction('Created knowledge row');
    };

    const removeRow = (index) => {
        setList((prev) => prev.filter((_, i) => i !== index));
        logKnowledgeAction('Deleted knowledge row', { index });
    };

    const uploadBanner = async (file) => {
        if (!file) return;
        const formData = new FormData();
        formData.append('image', file);
        setUploading(true);
        try {
            const res = await uploadSettingsImageApi(formData);
            if (!res?.success || !res?.data?.url) throw new Error(res?.message || 'Upload failed');
            setModal((m) => ({ ...m, draft: { ...(m.draft || {}), bannerImage: res.data.url } }));
        } catch (e) {
            toastMessage(e?.message || 'Upload failed', 'error');
        } finally {
            setUploading(false);
        }
    };

    const save = async (opts = { silent: false, refreshAfter: true }) => {
        setSaving(true);
        try {
            const normalizeContentBlocks = (blocks) => {
                const list = Array.isArray(blocks) ? blocks : [];
                return list.map((b) => {
                    const type = String(b?.type || 'text');
                    if (type === 'image') {
                        const links = Array.isArray(b?.link) ? b.link : (b?.link ? [b.link] : []);
                        return {
                            type: 'image',
                            link: links.map((x) => String(x || '').trim()).filter(Boolean),
                        };
                    }
                    if (type === 'banner') {
                        const links = Array.isArray(b?.link) ? b.link : (b?.link ? [b.link] : []);
                        return {
                            type: 'banner',
                            link: links.map((x) => String(x || '').trim()).filter(Boolean),
                        };
                    }
                    return {
                        type: 'text',
                        content: String(b?.content || ''),
                    };
                });
            };

            const normalized = (list || []).map((x) => ({
                title: x?.title || '',
                description: x?.description || '',
                createdAt: x?.createdAt || new Date().toISOString(),
                bannerImage: x?.bannerImage || '',
                content: normalizeContentBlocks(x?.content),
            }));
            const response = await updateUserMasterSettingsApi('knowledgeData', normalized);
            if (!response?.success) throw new Error(response?.message || 'Failed to save knowledgeData');
            if (!opts?.silent) toastMessage(response?.message || 'Knowledge saved', 'success');
            if (opts?.refreshAfter) await load();
            if (!opts?.silent) logKnowledgeAction('Saved knowledge data');
        } catch (e) {
            toastMessage(e?.message || 'Failed to save knowledgeData', 'error');
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (!didLoadKnowledge.current) return;
        if (uploading) return;

        if (knowledgeAutoSaveTimer.current) clearTimeout(knowledgeAutoSaveTimer.current);
        knowledgeAutoSaveTimer.current = setTimeout(() => {
            save({ silent: true, refreshAfter: false });
        }, 800);

        return () => {
            if (knowledgeAutoSaveTimer.current) clearTimeout(knowledgeAutoSaveTimer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [list, uploading]);

    const applyModal = () => {
        setList((prev) => prev.map((r, i) => (i === modal.index ? { ...modal.draft } : r)));
        logKnowledgeAction('Edited knowledge row', { index: modal.index, title: modal.draft?.title || '' });
        setModal({ open: false, index: -1, draft: null });
    };

    const openPreview = (row) => setPreviewModal({ open: true, row: JSON.parse(JSON.stringify(row || {})) });
    const closePreview = () => setPreviewModal({ open: false, row: null });

    const addContentBlock = () => {
        setModal((m) => ({
            ...m,
            draft: {
                ...(m.draft || {}),
                content: [...(m.draft?.content || []), { type: 'text', content: '' }],
            },
        }));
        logKnowledgeAction('Created content block', { index: modal.index });
    };

    const updateContentBlock = (idx, patch) => {
        setModal((m) => {
            const next = [...(m.draft?.content || [])];
            next[idx] = { ...(next[idx] || {}), ...patch };
            return { ...m, draft: { ...(m.draft || {}), content: next } };
        });
    };

    const removeContentBlock = (idx) => {
        setModal((m) => {
            const next = (m.draft?.content || []).filter((_, i) => i !== idx);
            return { ...m, draft: { ...(m.draft || {}), content: next } };
        });
        logKnowledgeAction('Deleted content block', { rowIndex: modal.index, blockIndex: idx });
    };

    const updateImageLinkAt = (blockIdx, linkIdx, value) => {
        setModal((m) => {
            const nextBlocks = [...(m.draft?.content || [])];
            const block = { ...(nextBlocks[blockIdx] || {}) };
            const links = Array.isArray(block.link) ? [...block.link] : (block.link ? [block.link] : ['']);
            links[linkIdx] = value;
            block.link = links;
            nextBlocks[blockIdx] = block;
            return { ...m, draft: { ...(m.draft || {}), content: nextBlocks } };
        });
    };

    const addImageLink = (blockIdx) => {
        setModal((m) => {
            const nextBlocks = [...(m.draft?.content || [])];
            const block = { ...(nextBlocks[blockIdx] || {}) };
            const links = Array.isArray(block.link) ? [...block.link] : (block.link ? [block.link] : []);
            links.push('');
            block.link = links;
            nextBlocks[blockIdx] = block;
            return { ...m, draft: { ...(m.draft || {}), content: nextBlocks } };
        });
    };

    const removeImageLink = (blockIdx, linkIdx) => {
        setModal((m) => {
            const nextBlocks = [...(m.draft?.content || [])];
            const block = { ...(nextBlocks[blockIdx] || {}) };
            const links = Array.isArray(block.link) ? [...block.link] : (block.link ? [block.link] : []);
            const filtered = links.filter((_, i) => i !== linkIdx);
            block.link = filtered.length ? filtered : [''];
            nextBlocks[blockIdx] = block;
            return { ...m, draft: { ...(m.draft || {}), content: nextBlocks } };
        });
    };

    const uploadContentImage = async (blockIdx, linkIdx, file) => {
        if (!file) return;
        const formData = new FormData();
        formData.append('image', file);
        setUploading(true);
        try {
            const res = await uploadSettingsImageApi(formData);
            if (!res?.success || !res?.data?.url) throw new Error(res?.message || 'Upload failed');
            updateImageLinkAt(blockIdx, linkIdx, res.data.url);
            toastMessage('Image uploaded successfully', 'success');
        } catch (e) {
            toastMessage(e?.message || 'Upload failed', 'error');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="card shadow-sm">
            <div className="card-body" style={{ padding: 0 }}>
                <div style={{ padding: 16 }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Knowledge Data</h5>
                        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addNew}>Add Knowledge</Button>
                    </div>
                  
                </div>
                <div className="table-responsive" style={{ padding: '0 16px 16px' }}>
                    <table className="table table-bordered align-middle mb-0" style={{ width: '100%', margin: 0, borderColor: SETTINGS_TABLE_BORDER }}>
                        <thead>
                            <tr>
                                <th style={{ width: 70 }}>S. No.</th>
                                <th style={{ minWidth: 180 }}>Title</th>
                                <th style={{ minWidth: 260 }}>Description</th>
                                <th style={{ width: 140 }}>Banner</th>
                                <th style={{ minWidth: 220 }}>Content Blocks</th>
                                <th style={{ width: 180 }}>Created</th>
                                <th style={{ width: 120 }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.length === 0 ? (
                                <tr><td colSpan={7} className="text-center text-muted py-4">No knowledge items</td></tr>
                            ) : list.map((r, idx) => (
                                <tr key={idx}>
                                    <td>{idx + 1}</td>
                                    <td>{r?.title ? r.title : BLANK}</td>
                                    <td style={{ whiteSpace: 'normal' }}>{r?.description ? r.description : BLANK}</td>
                                    <td>
                                        {r?.bannerImage ? (
                                            <img src={r.bannerImage} alt="" style={{ width: 56, height: 32, borderRadius: 8, objectFit: 'cover', border: '1px solid #e5e7eb' }} />
                                        ) : BLANK}
                                    </td>
                                    <td style={{ whiteSpace: 'normal' }}>
                                        {Array.isArray(r?.content) && r.content.length
                                            ? r.content.map((b, i) => {
                                                const type = String(b?.type || 'text');
                                                const count = Array.isArray(b?.link) ? b.link.filter(Boolean).length : (b?.link ? 1 : 0);
                                                if (type === 'text') return `#${i + 1} text`;
                                                return `#${i + 1} ${type} (${count} link${count === 1 ? '' : 's'})`;
                                            }).join(', ')
                                            : BLANK}
                                        <div className="mt-2">
                                            <Button size="small" variant="outlined" onClick={() => openPreview(r)}>
                                                View Details
                                            </Button>
                                        </div>
                                    </td>
                                    <td>{r?.createdAt ? String(r.createdAt).slice(0, 10) : BLANK}</td>
                                    <td>
                                        <IconButton size="small" onClick={() => openEdit(idx)} title="Edit"><EditIcon fontSize="small" /></IconButton>
                                        <IconButton size="small" onClick={() => removeRow(idx)} title="Delete"><DeleteIcon fontSize="small" /></IconButton>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div style={{ padding: '0 16px 16px' }}>
                    <button type="button" className="btn w-100" style={SETTINGS_BUTTON_STYLE} onClick={save} disabled={saving}>
                        {saving ? 'Saving...' : 'Save Knowledge Data'}
                    </button>
                </div>
            </div>

            <Dialog open={modal.open} onClose={() => setModal({ open: false, index: -1, draft: null })} maxWidth="md" fullWidth>
                <DialogTitle>Edit Knowledge Item</DialogTitle>
                <DialogContent dividers>
                    <div className="row g-2">
                        <div className="col-12">
                            <TextField label="Title" value={modal.draft?.title || ''} onChange={(e) => setModal((m) => ({ ...m, draft: { ...(m.draft || {}), title: e.target.value } }))} fullWidth />
                        </div>
                        <div className="col-12">
                            <TextField label="Description" value={modal.draft?.description || ''} onChange={(e) => setModal((m) => ({ ...m, draft: { ...(m.draft || {}), description: e.target.value } }))} fullWidth multiline minRows={2} />
                        </div>
                        <div className="col-md-6">
                            <TextField label="Created At (ISO)" value={modal.draft?.createdAt || ''} onChange={(e) => setModal((m) => ({ ...m, draft: { ...(m.draft || {}), createdAt: e.target.value } }))} fullWidth />
                        </div>
                        <div className="col-md-6">
                            <input type="file" accept="image/*" className="form-control mt-2" onChange={(e) => uploadBanner(e.target.files?.[0])} />
                            {uploading ? <small className="text-muted d-block mt-1">Uploading...</small> : null}
                            {!!modal.draft?.bannerImage && (
                                <TextField
                                    className="mt-2"
                                    label="Banner Image URL"
                                    value={modal.draft?.bannerImage || ''}
                                    onChange={(e) => setModal((m) => ({ ...m, draft: { ...(m.draft || {}), bannerImage: e.target.value } }))}
                                    fullWidth
                                />
                            )}
                        </div>

                        <div className="col-12">
                            <div className="d-flex justify-content-between align-items-center mt-2 mb-2">
                                <strong>Content blocks</strong>
                                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addContentBlock}>Add Block</Button>
                            </div>
                            {(modal.draft?.content || []).length === 0 ? (
                                <div className="text-muted">No blocks</div>
                            ) : (modal.draft.content || []).map((b, i) => (
                                <div key={i} className="border rounded p-2 mb-2">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <TextField
                                            select
                                            label="Type"
                                            value={b?.type || 'text'}
                                            onChange={(e) => {
                                                const nextType = e.target.value;
                                                if (nextType === 'image') {
                                                    updateContentBlock(i, { type: nextType, link: Array.isArray(b?.link) ? b.link : [''], content: undefined });
                                                } else if (nextType === 'banner') {
                                                    updateContentBlock(i, { type: nextType, link: Array.isArray(b?.link) ? b.link : (b?.link ? [b.link] : ['']), content: undefined });
                                                } else {
                                                    updateContentBlock(i, { type: nextType, content: b?.content || '', link: '' });
                                                }
                                            }}
                                            size="small"
                                            style={{ minWidth: 180 }}
                                        >
                                            <MenuItem value="text">text</MenuItem>
                                            <MenuItem value="image">image</MenuItem>
                                            <MenuItem value="banner">banner</MenuItem>
                                        </TextField>
                                        <Button size="small" color="error" onClick={() => removeContentBlock(i)}>Remove</Button>
                                    </div>
                                    {String(b?.type || 'text') === 'text' ? (
                                        <TextField
                                            label="content"
                                            value={b?.content || ''}
                                            onChange={(e) => updateContentBlock(i, { content: e.target.value })}
                                            fullWidth
                                            multiline
                                            minRows={2}
                                        />
                                    ) : String(b?.type || '') === 'image' ? (
                                        <div className="d-flex flex-column gap-2">
                                            {(Array.isArray(b?.link) ? b.link : (b?.link ? [b.link] : [''])).map((linkItem, linkIdx) => (
                                                <div key={linkIdx} className="d-flex align-items-center gap-2">
                                                    <div className="d-flex flex-column flex-grow-1">
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            className="form-control"
                                                            onChange={(e) => uploadContentImage(i, linkIdx, e.target.files?.[0])}
                                                        />
                                                        {!!linkItem && (
                                                            <TextField
                                                                label={`Image URL ${linkIdx + 1}`}
                                                                value={linkItem || ''}
                                                                onChange={(e) => updateImageLinkAt(i, linkIdx, e.target.value)}
                                                                fullWidth
                                                                className="mt-2"
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                            <div>
                                                <Button size="small" variant="outlined" onClick={() => addImageLink(i)}>
                                                    Add Link
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="d-flex flex-column gap-2">
                                            {(Array.isArray(b?.link) ? b.link : (b?.link ? [b.link] : [''])).map((linkItem, linkIdx) => (
                                                <div key={linkIdx} className="d-flex flex-column">
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        className="form-control"
                                                        onChange={(e) => uploadContentImage(i, linkIdx, e.target.files?.[0])}
                                                    />
                                                    {!!linkItem && (
                                                        <TextField
                                                            label={`Banner URL ${linkIdx + 1}`}
                                                            value={linkItem || ''}
                                                            onChange={(e) => updateImageLinkAt(i, linkIdx, e.target.value)}
                                                            fullWidth
                                                            className="mt-2"
                                                        />
                                                    )}
                                                </div>
                                            ))}
                                            <div>
                                                <Button size="small" variant="outlined" onClick={() => addImageLink(i)}>
                                                    Add Link
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModal({ open: false, index: -1, draft: null })} color="inherit">Cancel</Button>
                    <Button onClick={applyModal} variant="contained">Apply</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={previewModal.open} onClose={closePreview} maxWidth="md" fullWidth>
                <DialogTitle>Knowledge Content Details</DialogTitle>
                <DialogContent dividers>
                    <div className="mb-3">
                        <strong>Title:</strong> {previewModal.row?.title || BLANK}
                    </div>
                    <div className="mb-3">
                        <strong>Description:</strong>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{previewModal.row?.description || BLANK}</div>
                    </div>
                    <div className="mb-3">
                        <strong>Content Blocks:</strong>
                    </div>
                    {(previewModal.row?.content || []).length === 0 ? (
                        <div className="text-muted">No blocks</div>
                    ) : (
                        (previewModal.row.content || []).map((b, idx) => (
                            <div key={idx} className="border rounded p-2 mb-2">
                                <div><strong>#{idx + 1} Type:</strong> {b?.type || 'text'}</div>
                                {String(b?.type || 'text') === 'text' ? (
                                    <div style={{ whiteSpace: 'pre-wrap' }} className="mt-1">{b?.content || BLANK}</div>
                                ) : (
                                    <div className="mt-1">
                                        {(Array.isArray(b?.link) ? b.link : (b?.link ? [b.link] : [])).filter(Boolean).length === 0 ? BLANK : (
                                            (Array.isArray(b?.link) ? b.link : [b.link]).filter(Boolean).map((u, i) => (
                                                <div key={i} style={{ wordBreak: 'break-all' }}>{u}</div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closePreview} variant="contained">Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export const QuickQuerySettingsToggler = () => {
    const [data, setData] = useState({
        guide_me: {
            mainQuestion: '',
            subQuestions: [''],
        },
        quick_query: {},
    });
    const [saving, setSaving] = useState(false);
    const didLoadQuick = useRef(false);
    const quickAutoSaveTimer = useRef(null);
    const logQuickAction = (description, metadata = {}) => {
        safeTrack({
            action: 'UPDATE',
            module: 'Settings',
            description: `Quick Query: ${description}`,
            metadata: { section: 'quick_query', ...metadata },
            status: 'success',
            responseStatus: 200,
        });
    };

    const load = async () => {
        try {
            const res = await getUserMasterSettingsApi('quick_query');
            const stored = res?.data?.data ?? null;
            if (stored && typeof stored === 'object') {
                setData((prev) => ({
                    ...prev,
                    ...stored,
                    guide_me: {
                        ...prev.guide_me,
                        ...(stored.guide_me || {}),
                        subQuestions: Array.isArray(stored?.guide_me?.subQuestions) && stored.guide_me.subQuestions.length
                            ? stored.guide_me.subQuestions
                            : prev.guide_me.subQuestions,
                    },
                    quick_query: {
                        ...prev.quick_query,
                        ...(stored.quick_query || {}),
                    },
                }));
            }
        } catch (_e) {
            // ignore
        } finally {
            didLoadQuick.current = true;
        }
    };

    useEffect(() => { load(); }, []);

    const save = async (opts = { silent: false, refreshAfter: true }) => {
        setSaving(true);
        try {
            const payload = {
                guide_me: {
                    mainQuestion: data?.guide_me?.mainQuestion || '',
                    subQuestions: (data?.guide_me?.subQuestions || []).map((s) => String(s || '').trim()).filter(Boolean),
                },
                quick_query: { ...(data?.quick_query || {}) },
            };
            const r = await updateUserMasterSettingsApi('quick_query', payload);
            if (!r?.success) throw new Error(r?.message || 'Failed to save quick_query');
            if (!opts?.silent) toastMessage(r?.message || 'quick_query saved', 'success');
            if (opts?.refreshAfter) await load();
            if (!opts?.silent) logQuickAction('Saved quick query settings');
        } catch (e) {
            toastMessage(e?.message || 'Failed to save quick_query', 'error');
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (!didLoadQuick.current) return;
        if (quickAutoSaveTimer.current) clearTimeout(quickAutoSaveTimer.current);

        quickAutoSaveTimer.current = setTimeout(() => {
            save({ silent: true, refreshAfter: false });
        }, 700);

        return () => {
            if (quickAutoSaveTimer.current) clearTimeout(quickAutoSaveTimer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const [subModal, setSubModal] = useState({ open: false, index: -1, value: '' });
    const [kvModal, setKvModal] = useState({ open: false, mode: 'edit', key: '', value: '', originalKey: '' });
    const tableBorderColor = '#d8deea';

    const addSubQuestion = () => {
        setData((prev) => ({ ...prev, guide_me: { ...prev.guide_me, subQuestions: [...(prev.guide_me.subQuestions || []), ''] } }));
        const idx = (data.guide_me.subQuestions || []).length;
        setSubModal({ open: true, index: idx, value: '' });
        logQuickAction('Created sub question');
    };

    const openSubEdit = (index) => {
        const v = (data.guide_me.subQuestions || [])[index] || '';
        setSubModal({ open: true, index, value: v });
    };

    const applySub = () => {
        setData((prev) => {
            const next = [...(prev.guide_me.subQuestions || [])];
            next[subModal.index] = subModal.value;
            return { ...prev, guide_me: { ...prev.guide_me, subQuestions: next } };
        });
        logQuickAction('Edited sub question', { index: subModal.index });
        setSubModal({ open: false, index: -1, value: '' });
    };

    const deleteSub = (index) => {
        setData((prev) => ({
            ...prev,
            guide_me: { ...prev.guide_me, subQuestions: (prev.guide_me.subQuestions || []).filter((_, i) => i !== index) },
        }));
        logQuickAction('Deleted sub question', { index });
    };

    const openKvEdit = (key) => {
        setKvModal({ open: true, mode: 'edit', key, originalKey: key, value: data.quick_query?.[key] || '' });
    };

    const openKvAdd = () => {
        setKvModal({ open: true, mode: 'add', key: '', originalKey: '', value: '' });
    };

    const deleteKv = (key) => {
        setData((p) => {
            const next = { ...(p.quick_query || {}) };
            delete next[key];
            return { ...p, quick_query: next };
        });
        logQuickAction('Deleted quick_query key', { key });
    };

    const applyKv = () => {
        const nextKey = String(kvModal.key || '').trim();
        if (!nextKey) {
            toastMessage('Key is required', 'error');
            return;
        }
        setData((p) => {
            const next = { ...(p.quick_query || {}) };
            if (kvModal.mode === 'edit' && kvModal.originalKey && kvModal.originalKey !== nextKey) {
                delete next[kvModal.originalKey];
            }
            next[nextKey] = kvModal.value ?? '';
            return { ...p, quick_query: next };
        });
        logQuickAction(kvModal.mode === 'add' ? 'Created quick_query key' : 'Edited quick_query key', {
            key: nextKey,
            originalKey: kvModal.originalKey || '',
        });
        setKvModal({ open: false, mode: 'edit', key: '', value: '', originalKey: '' });
    };

    return (
        <div className="card shadow-sm">
            <div className="card-body" style={{ padding: 0 }}>
                <div style={{ padding: 16 }}>
                    <h5 className="mb-1">Quick Query Settings</h5>
                
                </div>

                <div style={{ padding: '0 0 16px' }}>
                    <div className="table-responsive">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0">Guide Me</h6>
                        </div>
                        <div className="border p-3 mb-3">
                            <TextField
                                label="Main Question"
                                value={data.guide_me.mainQuestion}
                                onChange={(e) => setData((p) => ({ ...p, guide_me: { ...p.guide_me, mainQuestion: e.target.value } }))}
                                fullWidth
                                size="small"
                            />
                        </div>

                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0">Sub Questions</h6>
                            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addSubQuestion}>Add</Button>
                        </div>
                        <table className="table table-bordered align-middle mb-3" style={{ width: '100%', margin: 0, borderColor: tableBorderColor, tableLayout: 'fixed' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 80, fontWeight: 600, fontSize: 13, borderColor: tableBorderColor, background: '#f4f7fc' }}>S. No.</th>
                                    <th style={{ fontWeight: 600, fontSize: 13, borderColor: tableBorderColor, background: '#f4f7fc' }}>Question</th>
                                    <th style={{ width: 140, fontWeight: 600, fontSize: 13, borderColor: tableBorderColor, background: '#f4f7fc' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(data.guide_me.subQuestions || []).length === 0 ? (
                                    <tr><td colSpan={3} className="text-center text-muted py-4">No sub questions</td></tr>
                                ) : (data.guide_me.subQuestions || []).map((q, i) => (
                                    <tr key={i}>
                                        <td style={{ borderColor: tableBorderColor }}>{i + 1}</td>
                                        <td style={{ borderColor: tableBorderColor }}>{q ? q : BLANK}</td>
                                        <td style={{ borderColor: tableBorderColor }}>
                                            <IconButton size="small" onClick={() => openSubEdit(i)} title="Edit"><EditIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" onClick={() => deleteSub(i)} title="Delete"><DeleteIcon fontSize="small" /></IconButton>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0">Quick Query</h6>
                            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openKvAdd}>Add</Button>
                        </div>
                        <table className="table table-bordered align-middle mb-0" style={{ width: '100%', margin: 0, borderColor: tableBorderColor, tableLayout: 'fixed' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 80, fontWeight: 600, fontSize: 13, borderColor: tableBorderColor, background: '#f4f7fc' }}>S. No.</th>
                                    <th style={{ width: 280, fontWeight: 600, fontSize: 13, borderColor: tableBorderColor, background: '#f4f7fc' }}>Key</th>
                                    <th style={{ fontWeight: 600, fontSize: 13, borderColor: tableBorderColor, background: '#f4f7fc' }}>Value</th>
                                    <th style={{ width: 140, fontWeight: 600, fontSize: 13, borderColor: tableBorderColor, background: '#f4f7fc' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(data.quick_query || {}).map(([k, v], idx) => (
                                    <tr key={k}>
                                        <td style={{ borderColor: tableBorderColor }}>{idx + 1}</td>
                                        <td style={{ paddingLeft: 16, borderColor: tableBorderColor }}>{k}</td>
                                        <td style={{ paddingLeft: 16, paddingRight: 16, maxWidth: 900, wordBreak: 'break-word', borderColor: tableBorderColor }}>{v ? v : BLANK}</td>
                                        <td style={{ borderColor: tableBorderColor }}>
                                            <IconButton size="small" onClick={() => openKvEdit(k)} title="Edit"><EditIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" onClick={() => deleteKv(k)} title="Delete"><DeleteIcon fontSize="small" /></IconButton>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div style={{ padding: '0 0 16px' }}>
                    <button
                        type="button"
                        className="btn w-100"
                        onClick={save}
                        disabled={saving}
                        style={SETTINGS_BUTTON_STYLE}
                    >
                        {saving ? 'Saving...' : 'Save Quick Query Settings'}
                    </button>
                </div>
            </div>

            <Dialog open={subModal.open} onClose={() => setSubModal({ open: false, index: -1, value: '' })} maxWidth="sm" fullWidth>
                <DialogTitle>Edit subQuestions[{subModal.index}]</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        label="Question"
                        value={subModal.value}
                        onChange={(e) => setSubModal((m) => ({ ...m, value: e.target.value }))}
                        fullWidth
                        multiline
                        minRows={3}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSubModal({ open: false, index: -1, value: '' })} color="inherit">Cancel</Button>
                    <Button onClick={applySub} variant="contained">Apply</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={kvModal.open} onClose={() => setKvModal({ open: false, mode: 'edit', key: '', value: '', originalKey: '' })} maxWidth="sm" fullWidth>
                <DialogTitle>{kvModal.mode === 'add' ? 'Add Key/Value' : `Edit ${kvModal.originalKey}`}</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        label="Key"
                        value={kvModal.key}
                        onChange={(e) => setKvModal((m) => ({ ...m, key: e.target.value }))}
                        fullWidth
                        className="mb-2"
                    />
                    <TextField
                        label="Value"
                        value={kvModal.value}
                        onChange={(e) => setKvModal((m) => ({ ...m, value: e.target.value }))}
                        fullWidth
                        multiline
                        minRows={3}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setKvModal({ open: false, mode: 'edit', key: '', value: '', originalKey: '' })} color="inherit">Cancel</Button>
                    <Button onClick={applyKv} variant="contained">Apply</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export const ConsultantHomeBannerDataToggler = () => {
    const [data, setData] = useState({});
    const [saving, setSaving] = useState(false);
    const didLoadConsultantBanner = useRef(false);
    const consultantBannerAutoSaveTimer = useRef(null);
    const tableBorderColor = '#d8deea';
    const logConsultantBannerAction = (description, metadata = {}) => {
        safeTrack({
            action: 'UPDATE',
            module: 'Settings',
            description: `Consultant Banner: ${description}`,
            metadata: { section: 'consultant_home_banner_data', ...metadata },
            status: 'success',
            responseStatus: 200,
        });
    };

    const load = async () => {
        try {
            const res = await getUserMasterSettingsApi('consultant_home_banner_data');
            const stored = res?.data?.data ?? null;
            if (stored && typeof stored === 'object') {
                setData((prev) => ({
                    ...prev,
                    ...stored,
                }));
            }
        } catch (_e) {
            // ignore
        } finally {
            didLoadConsultantBanner.current = true;
        }
    };

    useEffect(() => { load(); }, []);

    const save = async (opts = { silent: false, refreshAfter: true }) => {
        setSaving(true);
        try {
            const payload = { ...(data || {}) };
            const r = await updateUserMasterSettingsApi('consultant_home_banner_data', payload);
            if (!r?.success) throw new Error(r?.message || 'Failed to save consultant_home_banner_data');
            if (!opts?.silent) toastMessage(r?.message || 'consultant_home_banner_data saved', 'success');
            if (opts?.refreshAfter) await load();
            if (!opts?.silent) logConsultantBannerAction('Saved consultant banner settings');
        } catch (e) {
            toastMessage(e?.message || 'Failed to save consultant_home_banner_data', 'error');
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (!didLoadConsultantBanner.current) return;
        if (consultantBannerAutoSaveTimer.current) clearTimeout(consultantBannerAutoSaveTimer.current);

        consultantBannerAutoSaveTimer.current = setTimeout(() => {
            save({ silent: true, refreshAfter: false });
        }, 700);

        return () => {
            if (consultantBannerAutoSaveTimer.current) clearTimeout(consultantBannerAutoSaveTimer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const [modal, setModal] = useState({ open: false, mode: 'edit', key: '', value: '', originalKey: '' });

    const openEdit = (key) => setModal({ open: true, mode: 'edit', key, originalKey: key, value: data[key] || '' });
    const openAdd = () => setModal({ open: true, mode: 'add', key: '', originalKey: '', value: '' });
    const deleteKey = (key) => {
        setData((p) => {
            const next = { ...(p || {}) };
            delete next[key];
            return next;
        });
        toastMessage(`Deleted "${key}"`, 'success');
        logConsultantBannerAction('Deleted key', { key });
    };
    const apply = () => {
        const nextKey = String(modal.key || '').trim();
        if (!nextKey) {
            toastMessage('Key is required', 'error');
            return;
        }
        setData((p) => {
            const next = { ...(p || {}) };
            if (modal.mode === 'edit' && modal.originalKey && modal.originalKey !== nextKey) {
                delete next[modal.originalKey];
            }
            next[nextKey] = modal.value ?? '';
            return next;
        });
        logConsultantBannerAction(modal.mode === 'add' ? 'Created key' : 'Edited key', {
            key: nextKey,
            originalKey: modal.originalKey || '',
        });
        setModal({ open: false, mode: 'edit', key: '', value: '', originalKey: '' });
    };

    const keys = Object.keys(data || {});

    return (
        <div className="card shadow-sm">
            <div className="card-body" style={{ padding: 0 }}>
                <div style={{ padding: 16 }}>
                    <h5 className="mb-1">Consultant Home Banner Data</h5>
                  
                </div>
                <div className="table-responsive" style={{ padding: '0 16px 16px', width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
                    <table className="table table-bordered align-middle mb-0" style={{ width: '100%', minWidth: '100%', margin: 0, borderColor: tableBorderColor, tableLayout: 'fixed' }}>
                        <thead>
                            <tr>
                                <th style={{ width: 80, fontWeight: 600, fontSize: 13, borderColor: tableBorderColor, background: '#f4f7fc' }}>S. No.</th>
                                <th style={{ width: 280, fontWeight: 600, fontSize: 13, borderColor: tableBorderColor, background: '#f4f7fc' }}>Key</th>
                                <th style={{ fontWeight: 600, fontSize: 13, borderColor: tableBorderColor, background: '#f4f7fc' }}>Value</th>
                                <th style={{ width: 120, fontWeight: 600, fontSize: 13, borderColor: tableBorderColor, background: '#f4f7fc' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {keys.map((k, idx) => (
                                <tr key={k}>
                                    <td style={{ borderColor: tableBorderColor }}>{idx + 1}</td>
                                    <td style={{ wordBreak: 'break-word', borderColor: tableBorderColor }}>{k}</td>
                                    <td style={{ paddingLeft: 16, paddingRight: 16, wordBreak: 'break-word', whiteSpace: 'normal', borderColor: tableBorderColor }}>
                                        {data[k] ? data[k] : BLANK}
                                    </td>
                                    <td style={{ borderColor: tableBorderColor }}>
                                        <IconButton size="small" onClick={() => openEdit(k)} title="Edit"><EditIcon fontSize="small" /></IconButton>
                                        <IconButton size="small" onClick={() => deleteKey(k)} title="Delete"><DeleteIcon fontSize="small" /></IconButton>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div style={{ padding: '0 16px 16px' }}>
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openAdd}>Add Key</Button>
                </div>
                <div style={{ padding: '0 16px 16px' }}>
                    <button
                        type="button"
                        className="btn w-100"
                        onClick={save}
                        disabled={saving}
                        style={SETTINGS_BUTTON_STYLE}
                    >
                        {saving ? 'Saving...' : 'Save Consultant Home Banner Data'}
                    </button>
                </div>
            </div>

            <Dialog open={modal.open} onClose={() => setModal({ open: false, mode: 'edit', key: '', value: '', originalKey: '' })} maxWidth="sm" fullWidth>
                <DialogTitle>{modal.mode === 'add' ? 'Add Key/Value' : `Edit ${modal.originalKey}`}</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        label="Key"
                        value={modal.key}
                        onChange={(e) => setModal((m) => ({ ...m, key: e.target.value }))}
                        fullWidth
                        className="mb-2"
                    />
                    <TextField
                        label="Value"
                        value={modal.value}
                        onChange={(e) => setModal((m) => ({ ...m, value: e.target.value }))}
                        fullWidth
                        multiline
                        minRows={3}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModal({ open: false, mode: 'edit', key: '', value: '', originalKey: '' })} color="inherit">Cancel</Button>
                    <Button onClick={apply} variant="contained">Apply</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};